import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Input,
  Label,
  HelpText,
  Select,
} from "@twilio-paste/core";
import {
  Modal,
  ModalHeader,
  ModalHeading,
  ModalBody,
  ModalFooter,
} from "@twilio-paste/modal";
import { AppState } from "../../store";
import {
  executeConversationAction,
  fetchOperators,
  setConversationAction,
} from "../../store/action-creators";
import { OperatorDto } from "../../dto/postLogin";
import {
  ConversationActionState,
  ConversationActionType,
} from "../../store/reducers/conversationActionsReducer";
import { useOperatorPermissions } from "../../hooks/useOperatorPermissions";
import { fetchUnlockReasons } from "../../store/action-creators/unlockReasonsActions";
import { ConversationState } from "../../dto/conversation";

interface ConversationActionsProps {
  conversationId: string;
}

const ConversationActions: React.FC<ConversationActionsProps> = ({
  conversationId,
}) => {
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);

  const operatorsState = useSelector((s: AppState) => s.operators);
  const conversationAction = useSelector((s: AppState) => s.conversationAction);

  const unlockReasonsState = useSelector((s: AppState) => s.unlockReasons);
  const unlockReasons = unlockReasonsState.reasons ?? [];

  const operators: OperatorDto[] = operatorsState.items ?? [];
  const operatorsLoading = operatorsState.isLoading;
  const convo = useSelector((s: AppState) =>
    (s.conversations?.items ?? []).find((c: any) => c.id === conversationId)
  ) as any | undefined;

  const { canAssign, canTransfer, canClose } = useOperatorPermissions();

  const isPending = convo?.state === ConversationState.Pending;
  const canShowAssign = canAssign && isPending;
  const canShowTransfer = canTransfer && !isPending;

  const hasAnyAction = canShowAssign || canShowTransfer || canClose;

  if (!hasAnyAction) return null;

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  useEffect(() => {
    if (!isOpen) return;

    const tasks: Promise<any>[] = [];
    if (operators.length === 0) tasks.push((dispatch as any)(fetchOperators()));
    if (unlockReasons.length === 0)
      tasks.push((dispatch as any)(fetchUnlockReasons()));
    if (!tasks.length) return;

    Promise.all(tasks).catch((e) => {
      console.error("Errore caricamento dati modale:", e?.message ?? e);
    });
  }, [isOpen, operators.length, unlockReasons.length, dispatch]);

  const updateConversationAction = (
    patch: Partial<ConversationActionState>
  ) => {
    const current = conversationAction;
    const next: ConversationActionState = {
      ...current,
      ...patch,
    };
    dispatch(setConversationAction(next));
  };

  const handleTypeChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
    const nextType = (e.target.value || null) as ConversationActionType;

    updateConversationAction({
      type: nextType,
      operator:
        nextType === "block" || nextType === "unblock"
          ? null
          : conversationAction.operator,
      unlockReasonId:
        nextType === "unblock" ? conversationAction.unlockReasonId : null,
      // quando è unblock non esiste più il campo "Motivo", quindi lo azzero
      reason: nextType === "unblock" ? "" : conversationAction.reason,
    });
  };

  const handleUnlockReasonIdChange: React.ChangeEventHandler<HTMLSelectElement> =
    (e) => {
      const v = e.target.value;
      const id = v ? Number(v) : null;
      updateConversationAction({
        unlockReasonId: Number.isFinite(id as number) ? (id as number) : null,
      });
    };

  const handleOperatorChange: React.ChangeEventHandler<HTMLSelectElement> = (
    e
  ) => {
    const selectedId = e.target.value || "";
    const op = operators.find((o) => o.id === selectedId) ?? null;
    updateConversationAction({ operator: op });
  };

  const handleReasonChange: React.ChangeEventHandler<HTMLInputElement> = (
    e
  ) => {
    updateConversationAction({ reason: e.target.value });
  };

  const { type, reason, operator, unlockReasonId } = conversationAction;

  const normalizedType: ConversationActionType | null = !type
    ? null
    : type === "transfer" && !canShowTransfer
    ? null
    : type === "assign" && !canShowAssign
    ? null
    : (type === "block" || type === "unblock") && !canClose
    ? null
    : type;

  const requiresOperator =
    normalizedType === "transfer" || normalizedType === "assign";

  const requiresUnlockReason = normalizedType === "unblock";
  const requiresNotes = normalizedType !== "unblock";

  const canConfirm =
    !!normalizedType &&
    (!requiresOperator || !!operator) &&
    (!requiresUnlockReason || !!unlockReasonId) &&
    (!requiresNotes || reason.trim().length > 0);

  const handleConfirm = () => {
    if (!canConfirm || !normalizedType) return;

    dispatch(
      setConversationAction({
        type: normalizedType,
        reason: normalizedType === "unblock" ? "" : reason,
        operator,
        unlockReasonId: normalizedType === "unblock" ? unlockReasonId : null,
      })
    );

    dispatch(executeConversationAction(conversationId));
    setIsOpen(false);
  };

  return (
    <>
      <Button variant="secondary" onClick={openModal}>
        Azioni
      </Button>

      <Modal
        isOpen={isOpen}
        onDismiss={closeModal}
        ariaLabelledby="conversation-actions-heading"
        size="default"
      >
        <ModalHeader>
          <ModalHeading as="h3" id="conversation-actions-heading">
            Azioni conversazione
          </ModalHeading>
        </ModalHeader>

        <ModalBody>
          <Box display="flex" flexDirection="column" rowGap="space60">
            <Box>
              <Label htmlFor="conversation-action-type">Azione</Label>
              <Select
                id="conversation-action-type"
                value={normalizedType ?? ""}
                onChange={handleTypeChange}
              >
                <option value="">Seleziona un'azione…</option>
                {canShowTransfer && (
                  <option value="transfer">Trasferisci</option>
                )}
                {canShowAssign && <option value="assign">Assegna</option>}
                {canClose && <option value="block">Blocca</option>}
                {canClose && <option value="unblock">Sblocca</option>}
              </Select>
              <HelpText>
                Scegli il tipo di azione da applicare alla conversazione.
              </HelpText>
            </Box>

            {requiresOperator && (
              <Box>
                <Label htmlFor="conversation-action-operator">Operatore</Label>
                <Select
                  id="conversation-action-operator"
                  value={operator?.id ?? ""}
                  onChange={handleOperatorChange}
                  disabled={operatorsLoading}
                >
                  <option value="">
                    {operatorsLoading
                      ? "Caricamento operatori…"
                      : "Seleziona un operatore…"}
                  </option>
                  {operators.map((op) => (
                    <option key={op.id} value={op.id}>
                      {op.name} ({op.teamName})
                    </option>
                  ))}
                </Select>
                <HelpText>
                  {operatorsLoading
                    ? "Sto recuperando la lista degli operatori…"
                    : "Seleziona l'operatore a cui assegnare/trasferire."}
                </HelpText>
              </Box>
            )}

            {normalizedType === "unblock" ? (
              <Box>
                <Label htmlFor="conversation-action-unlock-reason">
                  Motivo sblocco
                </Label>
                <Select
                  id="conversation-action-unlock-reason"
                  value={unlockReasonId != null ? String(unlockReasonId) : ""}
                  onChange={handleUnlockReasonIdChange}
                >
                  <option value="">Seleziona un motivo…</option>
                  {unlockReasons.map((r) => (
                    <option key={r.id} value={String(r.id)}>
                      {r.name}
                    </option>
                  ))}
                </Select>

                <HelpText>
                  {unlockReasonId
                    ? unlockReasons.find((r) => r.id === unlockReasonId)
                        ?.description ?? ""
                    : "Seleziona un motivo di sblocco."}
                </HelpText>
              </Box>
            ) : (
              <Box>
                <Label htmlFor="conversation-action-reason">Motivo</Label>
                <Input
                  id="conversation-action-reason"
                  type="text"
                  value={reason}
                  onChange={handleReasonChange}
                  placeholder="Inserisci il motivo dell'operazione"
                />
                <HelpText>
                  Il motivo sarà memorizzato come nota per questa azione.
                </HelpText>
              </Box>
            )}
          </Box>
        </ModalBody>

        <ModalFooter>
          <Box display="flex" columnGap="space40" justifyContent="flex-end">
            <Button variant="secondary" onClick={closeModal}>
              Annulla
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!canConfirm}
            >
              Conferma
            </Button>
          </Box>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ConversationActions;
